import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import QRCode from 'qrcode';
import {
  CalendarClock, CheckCircle, X, Loader2, AlertTriangle, ShieldCheck, Clock,
  MapPin, Stethoscope, FileText, Phone, User,
} from 'lucide-react';

/**
 * PatientAppointmentRequestPage — public status + confirmation page. Once
 * confirmed, renders a branded "Appointment Pass" with a QR code embedding
 * every detail so the patient can show it to reception / assistant /
 * doctor at the clinic.
 *
 * Route: /appointment-request/:id
 */
const SERVICE_LABEL = {
  consult:   'Consult',
  procedure: 'Minor Procedure',
  surgery:   'Surgery',
};

export default function PatientAppointmentRequestPage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/appointment-requests/${id}`);
      setR(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Request not found.');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Stop polling once status is terminal — saves a request every 10s.
    if (r && ['confirmed', 'declined', 'cancelled'].includes(r.status)) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load, r]);

  const confirm = async () => {
    if (!ack) { setError('Please tick the no-show acknowledgement.'); return; }
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/public/appointment-requests/${id}/confirm`, { no_show_acknowledged: true });
      setR(data);
    } catch (e) { setError(e?.response?.data?.detail || 'Could not confirm.'); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    const reason = window.prompt('Cancel this appointment? Add an optional note:', '');
    if (reason === null) return;
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/public/appointment-requests/${id}/cancel`, { reason });
      setR(data);
    } catch (e) { setError(e?.response?.data?.detail || 'Could not cancel.'); }
    finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-400 text-sm">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!r) {
    return (
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <p className="text-sm text-slate-600">{error || 'Request not found.'}</p>
      </div>
    );
  }

  // ── Confirmed → render the full Appointment Pass with QR. ──────────────
  if (r.status === 'confirmed' && r.offered_slot) {
    return <AppointmentPass r={r} />;
  }

  const slot = r.offered_slot;

  return (
    <div className="max-w-xl mx-auto px-4 py-8" data-testid="patient-appt-confirm">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="flex items-center gap-2">
          <CalendarClock size={18} className="text-amber-600" />
          <h1 className="text-lg font-semibold text-slate-900">Your appointment request</h1>
        </div>
        <DetailsBlock r={r} compact />

        {r.status === 'pending' && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-3 text-sm text-amber-900 flex items-start gap-2">
            <Clock size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Waiting for the doctor to offer a slot…</p>
              <p className="text-[12px] mt-0.5">This page refreshes every 10 seconds.</p>
            </div>
          </div>
        )}

        {r.status === 'offered' && slot && (
          <>
            <div className="rounded-xl bg-sky-50 border border-sky-200 px-4 py-3 text-sky-900">
              <p className="text-[11px] uppercase tracking-widest font-bold text-sky-700">Doctor has offered</p>
              <p className="text-lg font-bold mt-1 font-mono">{slot.date} · {slot.start_time}–{slot.end_time}</p>
              <p className="text-[12px] mt-0.5">{slot.duration_minutes}-minute exclusive window (2 back-to-back 15-min slots).</p>
            </div>

            <div className="rounded-xl border-2 border-rose-300 bg-rose-50 px-4 py-3 text-rose-900" data-testid="no-show-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="flex-shrink-0 mt-0.5 text-rose-600" />
                <div>
                  <p className="font-bold text-sm">Please read before confirming</p>
                  <p className="text-[13px] leading-relaxed mt-1">
                    Your doctor is travelling to this hospital <b>exclusively for you</b>. If you no-show, your DocSite rating drops <b>and a ₹{r.no_show_fee_inr || 500} fee will be added to your next consult fee with this doctor.</b>
                  </p>
                </div>
              </div>
              <label className="flex items-start gap-2 mt-3 cursor-pointer" data-testid="no-show-ack-label">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                  className="mt-1 accent-rose-600" data-testid="no-show-ack" />
                <span className="text-[12px] leading-snug">
                  I understand and agree. I'll reach during the offered window or cancel at least 6 hours in advance.
                </span>
              </label>
            </div>

            {error && <p className="text-xs text-rose-600">{error}</p>}

            <div className="flex gap-2">
              <button onClick={confirm} disabled={busy || !ack}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-3 bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                data-testid="confirm-btn">
                <ShieldCheck size={14} /> {busy ? 'Confirming…' : 'Confirm — freeze this slot'}
              </button>
              <button onClick={cancel} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-3 border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                data-testid="cancel-btn">
                <X size={14} /> Cancel
              </button>
            </div>
          </>
        )}

        {r.status === 'declined' && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-rose-900">
            <p className="font-bold text-sm">Doctor is unavailable</p>
            {r.decline_reason && <p className="text-[13px] mt-1">{r.decline_reason}</p>}
            <p className="text-[12px] mt-2">Please try a different doctor or check back later.</p>
          </div>
        )}

        {r.status === 'cancelled' && (
          <div className="rounded-xl bg-slate-100 border border-slate-200 px-4 py-3 text-slate-700">
            <p className="font-bold text-sm">This request was cancelled.</p>
          </div>
        )}
      </div>
    </div>
  );
}


function DetailsBlock({ r, compact }) {
  return (
    <div className={`text-sm text-slate-700 space-y-1 ${compact ? '' : ''}`}>
      <p><b className="text-slate-900">Hospital:</b> {r.location_name}</p>
      {r.notes_for_doctor && <p><b className="text-slate-900">Notes:</b> {r.notes_for_doctor}</p>}
    </div>
  );
}


/**
 * AppointmentPass — printable / screen-shottable confirmation card a patient
 * presents at the hospital reception. Includes a QR code that any DocSite
 * staff / doctor's assistant can scan to verify the booking instantly.
 */
function AppointmentPass({ r }) {
  const canvasRef = useRef(null);
  const slot = r.offered_slot || {};

  // Build the QR payload — JSON so a scanner-app (or DocSite's own scanner
  // in the future) can parse every field. URL falls back to the same
  // status page so an unknown scanner still lands on a verifiable page.
  const passUrl = typeof window !== 'undefined' ? `${window.location.origin}/appointment-request/${r.id}` : '';
  const qrPayload = JSON.stringify({
    type: 'docsite_appointment',
    id: r.id,
    patient: r.patient_name,
    phone_last4: r.patient_phone_last4,
    otp_verified: r.phone_otp_verified,
    doctor: r.doctor_name,
    hospital: r.location_name,
    city: r.location_city,
    service: r.service_type,
    date: slot.date,
    start: slot.start_time,
    end: slot.end_time,
    url: passUrl,
  });

  useEffect(() => {
    if (!canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, qrPayload, {
      width: 240,
      margin: 1,
      color: { dark: '#0f172a', light: '#ffffff' },
      errorCorrectionLevel: 'H',
    }).catch(() => { /* silent — QR is decorative if the lib fails */ });
  }, [qrPayload]);

  // Pretty-format the date — e.g., "Thu, 22 May 2026".
  const niceDate = (() => {
    if (!slot.date) return '';
    try {
      const d = new Date(slot.date);
      return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return slot.date; }
  })();

  return (
    <div className="max-w-md mx-auto px-4 py-8" data-testid="appointment-pass">
      <div className="rounded-2xl border-2 border-emerald-200 bg-white shadow-lg overflow-hidden">
        {/* Branded header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/docsite-logo.png" alt="DocSite" className="h-8 w-8 object-contain bg-white/10 rounded-md p-0.5" data-testid="pass-docsite-logo" />
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold opacity-90">DocSite Appointment Pass</p>
              <p className="text-sm font-bold leading-tight">On Appointment Basis</p>
            </div>
          </div>
          <CheckCircle size={20} />
        </div>

        {/* Slot */}
        <div className="px-5 py-4 bg-emerald-50/50 border-b border-emerald-100">
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Confirmed slot</p>
          <p className="text-xl font-bold text-slate-900 mt-1 font-mono">{niceDate}</p>
          <p className="text-base font-mono text-slate-700 mt-0.5">{slot.start_time}–{slot.end_time} <span className="text-xs text-slate-500">({slot.duration_minutes} min)</span></p>
        </div>

        {/* Patient + doctor + hospital */}
        <div className="px-5 py-4 space-y-2.5">
          <Row icon={<User size={14} />} label="Patient" value={r.patient_name} />
          <Row icon={<Phone size={14} />} label="Mobile (Booking)" value={
            <span className="font-mono">
              ●●●●●● {r.patient_phone_last4}
              {r.phone_otp_verified && (
                <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="pass-otp-verified">
                  <CheckCircle size={9} /> OTP Verified
                </span>
              )}
            </span>
          } />
          <Row icon={<Stethoscope size={14} />} label="Doctor" value={r.doctor_name} />
          <Row icon={<MapPin size={14} />} label="Hospital" value={r.location_name} />
          {(r.location_address || r.location_city) && (
            <Row icon={<MapPin size={14} className="opacity-0" />} label="Location" value={
              <span className="text-[12px]">{[r.location_address, r.location_city].filter(Boolean).join(', ')}</span>
            } />
          )}
          {r.location_city && <Row icon={<MapPin size={14} className="opacity-0" />} label="City" value={r.location_city} />}
          <Row icon={<CalendarClock size={14} />} label="Service" value={SERVICE_LABEL[r.service_type] || 'Consult'} />
          {r.notes_for_doctor && (
            <Row icon={<FileText size={14} />} label="Notes by patient" value={<span className="text-[12px]">{r.notes_for_doctor}</span>} />
          )}
        </div>

        {/* QR code */}
        <div className="px-5 pb-4 pt-2 flex flex-col items-center gap-2 border-t border-slate-100 bg-slate-50/40">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Show this to reception</p>
          <canvas ref={canvasRef} className="rounded bg-white p-2 border border-slate-200" data-testid="pass-qr" />
          <p className="text-[10px] text-slate-400 font-mono tracking-wider">Ref: {r.id.slice(0, 8).toUpperCase()}</p>
        </div>

        {/* DocSite footer brand */}
        <div className="px-5 py-2.5 bg-slate-900 text-white text-center flex items-center justify-center gap-1.5">
          <img src="/docsite-logo.png" alt="DocSite" className="h-4 w-4 object-contain" />
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold">Powered by DocSite Platform™</p>
        </div>
      </div>

      <div className="mt-4 text-center">
        <button onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50"
          data-testid="pass-print">
          Save / Print pass
        </button>
        <p className="text-[11px] text-slate-400 mt-2">Bookmark this page — your pass stays here until the appointment.</p>
      </div>
    </div>
  );
}


function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-slate-400 mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
        <div className="text-slate-900 font-medium">{value || '—'}</div>
      </div>
    </div>
  );
}
